import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { CTA, CardTitle, ConversionCTA, EASE, Section, SectionHeader } from "@/components/system";
import { Play, X, ChevronLeft, ChevronRight, Instagram } from "lucide-react";
import SEED_WORKS from "@/data/showcase-seed.json";

// Same-origin proxy via vercel.json rewrite — avoids browser extensions/shields blocking
// cross-origin requests to the Railway-hosted Strapi (Brave Shields, uBlock, etc.).
const STRAPI_URL = "/cms";

const FILTERS = ["All", "Performance", "Micro Drama", "Brand", "Regional Content"];

// Resilient local fallbacks (committed to the repo, served from Vercel) keyed by
// lowercased client name. Used only when the CMS has no image for an entry — any
// thumbnail uploaded in Strapi still takes priority. These were recovered from the
// source video frames after a CMS media wipe, so the showcase can never go blank.
const LOCAL_THUMBS = {
  "mazda": "/work/mazda.jpg",
  "pocket fm": "/work/pocket-fm.jpg",
  "jiohotstar": "/work/jiohotstar.jpg",
  "pokus ai": "/work/pokus-ai.jpg",
  "dashverse": "/work/dashverse.jpg",
  "edtech startup": "/work/edtech-startup.jpg",
  "educational ai influencer": "/work/educational-ai-influencer.jpg",
  "@mr.hellrocker": "/work/mr-hellrocker.jpg",
  "micro drama player": "/work/micro-drama-player.jpg",
  "turing": "/work/turing-frontier-fellows-thumbnail.png",
  "includ": "/work/includ-kids-fits-thumbnail.png",
};

// A few older Strapi media records still point at files that no longer exist
// in the Railway volume. Keep those cards local-first so a dead CMS URL cannot
// leave a blank tile while the browser waits for a 404 response.
const BROKEN_CMS_THUMBNAILS = new Set([
  "/uploads/small_Mazda_Poster_2fdc9b3ea7.png",
  "/uploads/small_IMTS_3_4_099752786e.png",
  "/uploads/small_Harry_Potter_Poster_1c8ac8afe6.png",
  "/uploads/small_Pokus_poster_0c90227f0c.png",
  "/uploads/small_inf_a102a713fd.png",
  "/uploads/small_Souvik_49e9f5d674.png",
]);

// CMS records may intentionally use a broad client label, but the showcase
// should give every piece a distinct, useful name. The key is the canonical
// video URL so the same naming stays stable whether the data comes from
// Strapi or the committed fallback snapshot.
const CONTENT_OVERRIDES = {
  "https://vimeo.com/1176901580": { title: "Pocket FM Acquisition Creative" },
  "https://vimeo.com/1176938391": { title: "Pokus AI Performance Creative" },
  "https://vimeo.com/1176948107": { title: "OTT Story Ad", categories: ["Performance", "Brand"] },
  "https://vimeo.com/1173515123": { title: "Brand Film System" },
  "https://www.instagram.com/reel/DUDuQrtkRM2": { title: "AI Influencer Ad" },
  "https://www.instagram.com/reel/DW5ov9zj8Sm": { title: "Viral Creator Campaign" },
  "https://vimeo.com/1187906979": { title: "Micro Drama Series", brand: "Story App Creative" },
  "https://vimeo.com/1187905860": { title: "Animated Educational Videos", brand: "Education Creative" },
  "https://vimeo.com/1220044374": { title: "Global Talent Campaign", categories: ["Performance", "Brand"] },
  "https://vimeo.com/1220044207": { title: "Commerce Product Creative", categories: ["Performance", "Brand"] },
  "https://drive.google.com/file/d/1IhHYLVcsXjqZQu_qrjCZuQEmkjDEP_af/view": { title: "Astrology Acquisition Ad" },
  "https://drive.google.com/file/d/1uGsgbfVaEmcSOdyfrWi9uBWuPtVBOHrJ/view": { title: "Beauty Acquisition Ad" },
  "https://drive.google.com/file/d/11KptLoiPix1ajUUYthVc6eUTNU9Aip05/view": { title: "Micro Drama Promo", brand: "Story App Creative" },
  "https://drive.google.com/file/d/1pKPDSZNDWjZqIMN_FoKKEsD1tm-3LwB5/view": {
    title: "Hindi Micro Drama",
    brand: "Hindi Story Creative",
    videoUrl: "https://drive.google.com/file/d/1VabtYTS-WgaW3-5ufIby0bHZyXhG9ARJ/view",
  },
  "https://drive.google.com/file/d/1VabtYTS-WgaW3-5ufIby0bHZyXhG9ARJ/view": {
    title: "Hindi Micro Drama",
    brand: "Hindi Story Creative",
  },
  "https://drive.google.com/file/d/1axRNG-omyYYc3aDh33F8gADFMuf45Tz0/view": { title: "Pocket FM Story Promo", brand: "Pocket FM" },
  "https://drive.google.com/file/d/1_RAFqKPT8XrLeuFJz8lQ6FQsNrzCG4P6/view": {
    title: "US Performance Promo",
    brand: "US Story App Creative",
    type: "Performance Creative",
    duration: "Horizontal performance ad",
    description: "A horizontal performance ad built to turn a high tension story into a clear paid acquisition hook.",
    category: "Performance",
    categories: ["Performance", "Micro Drama"],
    aspect: "landscape",
  },
  "https://drive.google.com/file/d/1a-T7ZtHAbkC23JjE9YO5VG3IeQrWhfJ6/view": {
    title: "Micro Drama Full Show",
    brand: "Micro Drama",
    type: "Micro Drama",
    duration: "Full show",
    description: "A complete micro drama show built around character, conflict, and episode level payoff.",
    category: "Micro Drama",
    aspect: "portrait",
  },
  "https://drive.google.com/file/d/1UhkJOxaIeJSjnive2p4kU2TiqAKsZ7oP/view": { title: "Animated Lesson Creative", brand: "Education Creative" },
  "https://drive.google.com/file/d/1KTsm51VJIzzZXfsx2xsdZpN7PqLXoY8i/view": { title: "Edutainment Explainer", brand: "Education Creative" },
  "https://drive.google.com/file/d/1XkA1Q_Jd3POYUgD8hsbAYOR4oDXFAs1V/view": { title: "History Story Creative", brand: "Education Creative" },
  "https://drive.google.com/file/d/1PedXPjS1jxeR612vXqJFtg_db6PiOdfR/view": {
    title: "History Learning Film",
    brand: "Education Creative",
    aspect: "portrait",
  },
  "https://drive.google.com/file/d/1Xp25sj5WHZ8UrK3Qc4y1k5vQVlqy8Ozn/view": { title: "Kannada Regional Ad" },
  "https://drive.google.com/file/d/1g_H81R8FN5ZfBKLXSTufciJOunH4HScx/view": { title: "Hindi Kissan Ad" },
  "https://drive.google.com/file/d/1p1GFA1DydMqkaeeLKwf1knlrMkuxrSFh/view": { title: "Telugu Regional Ad" },
  "https://drive.google.com/file/d/1qa7Xj68dQs5B1Z8-GoL_eCE47koPrpAN/view": { title: "Marathi Regional Ad" },
  "https://drive.google.com/file/d/1MECzuIi2fTZI-9LK_DCytxHIXDlHjnm8/view": {
    title: "Regional Performance Content",
    brand: "Regional Content",
  },
};

const THUMBNAIL_OVERRIDES = {
  "https://vimeo.com/1187905860": "/work/edtech-storytelling-thumbnail.png",
};

function canonicalVideoUrl(url) {
  return String(url || "").split("?")[0].split("#")[0].replace(/\/$/, "");
}

const FEATURED_ORDER = [
  "https://drive.google.com/file/d/1AsKbD13qvpRty9hEtMYuYnjqGHd7Y1RP/view", // Kitchen micro drama — leads the rail
  "https://vimeo.com/1173515123", // Mazda
  "https://vimeo.com/1176901580", // Pocket FM performance creative
  "https://vimeo.com/1176948107", // Hotstar
  "https://vimeo.com/1220044207", // Includ
  "https://vimeo.com/1220044374", // Turing
  "https://drive.google.com/file/d/1IhHYLVcsXjqZQu_qrjCZuQEmkjDEP_af/view", // Astrology Player
  "https://drive.google.com/file/d/1uGsgbfVaEmcSOdyfrWi9uBWuPtVBOHrJ/view", // Beauty Player
  "https://drive.google.com/file/d/1_RAFqKPT8XrLeuFJz8lQ6FQsNrzCG4P6/view", // US Promo Micro Drama
  "https://drive.google.com/file/d/1VabtYTS-WgaW3-5ufIby0bHZyXhG9ARJ/view", // Hindi Micro Drama
  "https://drive.google.com/file/d/1a-T7ZtHAbkC23JjE9YO5VG3IeQrWhfJ6/view", // Vertical Micro Drama
  "https://drive.google.com/file/d/1MECzuIi2fTZI-9LK_DCytxHIXDlHjnm8/view", // Regional Performance Content
].map(canonicalVideoUrl);

const FEATURED_ORDER_RANK = new Map(FEATURED_ORDER.map((url, index) => [url, index]));

const SHOWCASE_URLS = new Set(FEATURED_ORDER);

const HIDDEN_SHOWCASE_URLS = new Set([
  "https://drive.google.com/file/d/1UhkJOxaIeJSjnive2p4kU2TiqAKsZ7oP/view",
  "https://drive.google.com/file/d/1KTsm51VJIzzZXfsx2xsdZpN7PqLXoY8i/view",
  "https://drive.google.com/file/d/1PedXPjS1jxeR612vXqJFtg_db6PiOdfR/view",
  "https://vimeo.com/1187905860",
  "https://drive.google.com/file/d/1axRNG-omyYYc3aDh33F8gADFMuf45Tz0/view",
].map(canonicalVideoUrl));

function sortShowcaseItems(items, { featuredOnly = true } = {}) {
  const uniqueItems = [];
  const seen = new Set();
  for (const item of items) {
    const key = canonicalVideoUrl(item.videoUrl) || `id:${item.id}`;
    if (HIDDEN_SHOWCASE_URLS.has(canonicalVideoUrl(item.videoUrl))) continue;
    if (seen.has(key)) continue;
    seen.add(key);
    uniqueItems.push(item);
  }

  return uniqueItems.filter((item) => !featuredOnly || SHOWCASE_URLS.has(canonicalVideoUrl(item.videoUrl))).sort((a, b) => {
    const aRank = FEATURED_ORDER_RANK.get(canonicalVideoUrl(a.videoUrl));
    const bRank = FEATURED_ORDER_RANK.get(canonicalVideoUrl(b.videoUrl));
    if (aRank !== undefined || bRank !== undefined) {
      if (aRank === undefined) return 1;
      if (bRank === undefined) return -1;
      return aRank - bRank;
    }
    return (a.cmsOrder ?? 9999) - (b.cmsOrder ?? 9999) || String(a.id).localeCompare(String(b.id));
  });
}

// Vimeo's player reports the source dimensions through oEmbed, but fetching
// that metadata on every page load would make the portfolio depend on another
// network request. These known cuts are pinned locally; unknown videos safely
// fall back to the portrait card shape.
const PREVIEW_ASPECT_HINTS = {
  "1173515123": "landscape",
  "1176901580": "square",
  "1176948107": "portrait",
  "1176938391": "square",
  "1187906979": "portrait",
  "1187905860": "portrait",
  "1220044374": "landscape",
  "1220044207": "portrait",
};

function getEmbedUrl(url, { autoplay = true } = {}) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}?autoplay=${autoplay ? 1 : 0}&rel=0&modestbranding=1&playsinline=1`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) return `https://player.vimeo.com/video/${vm[1]}?autoplay=${autoplay ? 1 : 0}&playsinline=1&autopause=0`;
  const gd = url.match(/drive\.google\.com\/file\/d\/([^/]+)/);
  if (gd) return `https://drive.google.com/file/d/${gd[1]}/preview`;
  const ig = url.match(/instagram\.com\/(p|reel)\/([^/?]+)/);
  if (ig) return `https://www.instagram.com/${ig[1]}/${ig[2]}/embed/`;
  return null;
}

// Hover previews are deliberately narrower than the full player. Only sources
// that support silent autoplay are mounted here. Instagram and Drive stay on
// their poster until the visitor clicks into the full player.
function getPreviewEmbedUrl(url) {
  if (!url) return null;
  const yt = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&?/]+)/);
  if (yt) {
    return `https://www.youtube.com/embed/${yt[1]}?autoplay=1&mute=1&controls=0&rel=0&playsinline=1&loop=1&playlist=${yt[1]}`;
  }
  const vm = url.match(/vimeo\.com\/(\d+)/);
  if (vm) {
    return `https://player.vimeo.com/video/${vm[1]}?autoplay=1&muted=1&controls=0&title=0&byline=0&portrait=0&loop=1&background=1`;
  }
  return null;
}

function getPreviewAspect(url) {
  const match = url?.match(/vimeo\.com\/(\d+)/);
  return match ? PREVIEW_ASPECT_HINTS[match[1]] || "portrait" : "portrait";
}

function normalizeAspect(value) {
  const normalized = String(value || "").toLowerCase().replace(/\s/g, "");
  if (["16/9", "16:9", "landscape"].includes(normalized)) return "landscape";
  if (["1/1", "1:1", "square"].includes(normalized)) return "square";
  if (["9/16", "9:16", "portrait"].includes(normalized)) return "portrait";
  return null;
}

function cleanVisibleText(value) {
  return String(value || "")
    .replace(/\s+[-–—]\s+/g, " · ")
    .replace(/([A-Za-z])[-–—]([A-Za-z])/g, "$1 $2")
    .replace(/\s{2,}/g, " ")
    .trim();
}

function distinctMeta(brand, duration, title) {
  const values = [brand];
  const cleanBrand = String(brand || "").trim().toLowerCase();
  const cleanDuration = String(duration || "").trim().toLowerCase();
  const cleanTitle = String(title || "").trim().toLowerCase();
  if (duration && cleanDuration !== cleanBrand && cleanDuration !== cleanTitle) values.push(duration);
  return values.filter(Boolean).join(" · ");
}

function mapStrapiItem(item) {
  const cat = (item.type || "").toLowerCase();
  const category =
    cat.includes("show") || cat.includes("series") || cat.includes("drama")
      ? "Micro Drama"
      : cat.includes("education") || cat.includes("edtech")
      ? "Education"
      : cat.includes("regional") || cat.includes("language")
      ? "Regional Content"
      : cat.includes("ad") || cat.includes("performance") || cat.includes("creative")
      ? "Performance"
      : "Brand";
  const cmsImagePath = item.image?.formats?.small?.url || item.image?.url;
  const cmsImage = cmsImagePath
    ? cmsImagePath.startsWith("http") || cmsImagePath.startsWith("/work/")
      ? cmsImagePath
      : STRAPI_URL + cmsImagePath
    : "";
  const fallbackImage = LOCAL_THUMBS[(item.client || "").trim().toLowerCase()] || "";
  const override = CONTENT_OVERRIDES[canonicalVideoUrl(item.videoUrl)] || {};
  const effectiveVideoUrl = override.videoUrl || item.videoUrl || "";
  const thumbnailOverride = THUMBNAIL_OVERRIDES[canonicalVideoUrl(effectiveVideoUrl)] || "";
  const image =
    thumbnailOverride ||
    (BROKEN_CMS_THUMBNAILS.has(cmsImagePath) ? fallbackImage : cmsImage) ||
    fallbackImage ||
    "";
  const previewAspect = normalizeAspect(override.aspect) || normalizeAspect(item.aspectRatio || item.videoAspect) || getPreviewAspect(effectiveVideoUrl);
  return {
    id: item.id,
    // Prefer the CMS image, but if it fails to load (e.g. media wiped from the
    // Strapi volume) the <img onError> swaps to the committed local fallback.
    image,
    fallbackImage,
    title: cleanVisibleText(override.title || item.title || item.client),
    brand: cleanVisibleText(override.brand || item.client),
    type: cleanVisibleText(override.type || item.type),
    category: override.category || category,
    categories: [...new Set(override.categories || [override.category || category])],
    duration: cleanVisibleText(override.duration || item.timeframe || item.assets),
    description: cleanVisibleText(override.description || item.description),
    videoUrl: effectiveVideoUrl,
    isInstagram: item.isInstagram || false,
    instagramUrl: item.instagramUrl || "",
    caseStudyUrl: item.caseStudyUrl || "",
    niche: (item.niche || "").toLowerCase(),
    previewAspect,
    cmsOrder: Number(item.order ?? 9999),
  };
}

/* ── Work card ──────────────────────────────────────────────────────────────
   The old card stacked everything into one absolutely-positioned layer over
   the still: a full-tile `from-black` scrim so the overlay type had something
   to sit on, a brand badge, a "Preview" chip, a 56px solid-gold play disc dead
   centre, and the title block. The scrim veiled the frame edge to edge and the
   image ran at 90% on top of that, so the thing this section exists to show —
   the work — was the least visible object on the card.

   The metadata is a normal-flow caption below the frame now. That is the whole
   fix: the still runs clean at 100%, the badge and the chip lose their reason
   to exist, and the title can be set on the type scale at full contrast
   instead of being sized to survive a gradient.

   There is also one card layout, not two. `PRIORITY_BRANDS` used to give Mazda
   and JioHotstar an inverted hierarchy (brand as headline, title as label) —
   and the two branches passed different arguments to `distinctMeta`, so the
   same slot printed different data depending on the brand. Priority is
   expressed by position in FEATURED_ORDER, which is where ordering belongs. */
function Card({ item, onOpen, tabIndex, previewKey, isPreviewing, onPreviewStart, onPreviewEnd, priority = false }) {
  const timerRef = useRef(null);
  const previewEmbed = getPreviewEmbedUrl(item.videoUrl) || getPreviewEmbedUrl(item.instagramUrl);
  const localVideo = item?.videoUrl?.startsWith("/") ? item.videoUrl : null;
  const canPreview = Boolean(previewEmbed || localVideo);

  const clearPreviewIntent = () => {
    if (timerRef.current) {
      window.clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  };

  const handleMouseEnter = () => {
    if (!canPreview || !window.matchMedia("(hover: hover) and (pointer: fine)").matches) return;
    clearPreviewIntent();
    timerRef.current = window.setTimeout(() => {
      timerRef.current = null;
      onPreviewStart(previewKey);
    }, 260);
  };

  const handleMouseLeave = () => {
    clearPreviewIntent();
    onPreviewEnd(previewKey);
  };

  useEffect(() => () => clearPreviewIntent(), []);

  return (
    <button
      type="button"
      onClick={() => {
        handleMouseLeave();
        onOpen(item);
      }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      tabIndex={tabIndex}
      data-previewing={isPreviewing || undefined}
      /* The resting edge is what defines the card; .showcase-card brightens it
         to gold on hover, which is allowed here because the whole tile really
         is a button. */
      className="showcase-card group relative flex w-[260px] shrink-0 flex-col border border-line text-left md:w-[280px]"
      data-testid={`showcase-card-${item.id}`}
    >
      {/* One ratio for the whole rail. The source material spans 16:9, 1:1 and
          9:16, and the grid used to force 9:16 on all of it — which cropped the
          landscape brand films to a third of their frame and made the tile
          visibly change geometry the moment a preview mounted. 4:5 is the
          sanctioned alternative and is the shape that costs the least across
          the actual set. */}
      <div className="brand-frame w-full">
        <img
          src={item.image}
          alt={item.title}
          loading={priority ? "eager" : "lazy"}
          fetchPriority={priority ? "auto" : "low"}
          decoding="async"
          width={560}
          height={700}
          onError={(e) => {
            if (item.fallbackImage && e.target.src !== window.location.origin + item.fallbackImage) {
              e.target.src = item.fallbackImage;
            }
          }}
          className={`absolute inset-0 h-full w-full object-cover transition-opacity duration-reveal ease-gate ${isPreviewing ? "opacity-0" : "opacity-100"}`}
        />
        {isPreviewing && previewEmbed && (
          <iframe
            src={previewEmbed}
            title={`Preview of ${item.title}`}
            tabIndex="-1"
            aria-hidden="true"
            data-preview-aspect={item.previewAspect}
            className={`showcase-card-preview showcase-card-preview-iframe showcase-card-preview-${item.previewAspect} border-0`}
            frameBorder="0"
            allow="autoplay; encrypted-media"
          />
        )}
        {isPreviewing && localVideo && !previewEmbed && (
          <video
            src={localVideo}
            poster={item.image}
            muted
            autoPlay
            loop
            playsInline
            aria-hidden="true"
            className="showcase-card-preview absolute inset-0 h-full w-full object-cover"
          />
        )}

        {/* Small, neutral, off-centre. This was a 56px solid-gold disc in the
            middle of every still — roughly 33 of them in one viewport once the
            three-copy loop is counted, which is most of why the real gold CTA
            stopped reading as the action. It marks "this plays"; it is not
            itself the thing you came to click. */}
        <span
          aria-hidden="true"
          className="pointer-events-none absolute bottom-3 left-3 flex h-9 w-9 items-center justify-center border border-line bg-ground/80 text-bone"
        >
          <Play className="h-4 w-4" />
        </span>
      </div>

      {/* Caption in normal flow, at full contrast, on the type scale. */}
      <div className="flex flex-col gap-2 p-4">
        <p className="font-body text-caption text-mute">
          {distinctMeta(item.brand, item.duration, item.title)}
        </p>
        <CardTitle>{item.title}</CardTitle>
      </div>
    </button>
  );
}

/* ── Player ─────────────────────────────────────────────────────────────────
   The modal used to repeat the card's mistake on a playing video: a 96px
   70%-black gradient across the top of the frame, a brand chip and a title
   block floating on the picture (with a hand-tuned `right-16` so the title
   would clear the close button on phones — evidence the overlay was fighting
   the layout, not helping it). The info panel below the player is the correct
   home for all of it, so that is where it lives now and the frame is clean. */
function PlayerModal({ item, onClose }) {
  const prefersManualPlay = typeof window !== "undefined" && window.matchMedia("(hover: none), (pointer: coarse)").matches;
  const embedOptions = { autoplay: !prefersManualPlay };
  const embed = item ? getEmbedUrl(item.videoUrl, embedOptions) || getEmbedUrl(item.instagramUrl, embedOptions) : null;
  const localVideo = item?.videoUrl?.startsWith("/") ? item.videoUrl : null;
  const [showInfo, setShowInfo] = useState(false);
  const mediaAspect = item?.previewAspect || (item?.videoAspect === "16/9" ? "landscape" : "portrait");
  const isLandscape = mediaAspect === "landscape";
  const isSquare = mediaAspect === "square";
  const modalWidth = isLandscape ? "max-w-5xl" : isSquare ? "max-w-2xl" : "max-w-md";
  const mediaRatio = isLandscape ? "aspect-video" : isSquare ? "aspect-square" : "aspect-reel";
  // Keep the media box's width tied to its available height. A fixed width
  // combined with max-height used to distort portrait Drive embeds into a
  // wider frame, which made Drive add its own black letterboxing. The reserve
  // grew from 13rem to 15rem when the brand and title moved off the picture
  // and into the panel underneath it.
  const mediaHeight = "calc(100vh - 15rem)";
  const modalMaxWidth = isLandscape
    ? `min(72rem, calc(${mediaHeight} * 1.77777778))`
    : isSquare
    ? `min(40rem, ${mediaHeight})`
    : `min(28rem, calc(${mediaHeight} * 0.5625))`;
  const overlayRef = useRef(null);
  const closeBtnRef = useRef(null);

  // Dialog semantics: focus the close button on open, close on Escape, keep
  // Tab cycling inside the overlay, and restore focus to the opener on close.
  useEffect(() => {
    if (!item) return;
    const opener = document.activeElement;
    closeBtnRef.current?.focus();
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab" || !overlayRef.current) return;
      const focusables = overlayRef.current.querySelectorAll(
        'button, a[href], iframe, [tabindex]:not([tabindex="-1"])'
      );
      if (!focusables.length) return;
      const first = focusables[0];
      const last = focusables[focusables.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      // preventScroll: the modal's own conversion CTA must win the scroll,
      // not the focus restore back to the opener card.
      opener?.focus?.({ preventScroll: true });
    };
  }, [item, onClose]);

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          ref={overlayRef}
          role="dialog"
          aria-modal="true"
          aria-label={item ? `${item.brand}: ${item.title}` : "Creative preview"}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.3 }}
          className="fixed inset-0 z-[100] flex items-center justify-center bg-ground/95 p-4 backdrop-blur-md md:p-6"
          onClick={onClose}
        >
          {/* Dismiss is not the action this screen is selling, so it is not
              gold. It used to be a solid gold disc a few hundred pixels above
              the gold conversion button — two very different jobs in one
              colour. */}
          <button
            ref={closeBtnRef}
            onClick={onClose}
            className="fixed right-4 top-4 z-[110] flex h-12 w-12 items-center justify-center border border-line bg-ground text-bone transition-colors duration-hover ease-gate hover:border-bone md:right-6 md:top-6"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
          <motion.div
            initial={{ scale: 0.95, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.95, opacity: 0 }}
            transition={{ duration: 0.4, ease: EASE }}
            onClick={(e) => e.stopPropagation()}
            className={`relative flex w-full ${modalWidth} max-h-[calc(100vh-2rem)] flex-col overflow-hidden border border-line bg-ground`}
            style={{ maxWidth: modalMaxWidth }}
          >
            {/* Media — the player owns this whole box, with nothing on top of
                it. Every overlay that used to live here is in the panel below. */}
            <div className={`relative w-full shrink-0 bg-ground ${mediaRatio}`}>
              {embed ? (
                <iframe
                  src={embed}
                  title={item.title}
                  loading="eager"
                  className="absolute inset-0 h-full w-full"
                  frameBorder="0"
                  allow="autoplay; encrypted-media; fullscreen; picture-in-picture"
                  allowFullScreen
                />
              ) : localVideo ? (
                <video
                  src={localVideo}
                  poster={item.image}
                  className="absolute inset-0 h-full w-full object-cover"
                  controls
                  autoPlay
                  playsInline
                />
              ) : (
                <img
                  src={item.image}
                  alt={item.title}
                  onError={(e) => {
                    if (item.fallbackImage && e.target.src !== window.location.origin + item.fallbackImage) {
                      e.target.src = item.fallbackImage;
                    }
                  }}
                  className="absolute inset-0 h-full w-full object-cover"
                />
              )}
            </div>

            {/* Info panel BELOW the player — it must never cover the video,
                or it buries the embed's unmute/controls. */}
            <div className="overflow-y-auto border-t border-line bg-ground p-6">
              <p className="font-body text-caption text-mute">
                {distinctMeta(item.brand, item.type, item.title)}
              </p>
              <CardTitle className="mt-2">{item.title}</CardTitle>

              <button
                onClick={() => setShowInfo((v) => !v)}
                className="mt-5 w-full text-left"
              >
                <div className="mb-2 flex items-center justify-between gap-4">
                  {/* Production metadata is one of the two things the mono
                      label is still allowed to do. */}
                  <span className="font-label text-label uppercase text-faint">
                    {item.duration}
                  </span>
                  <span className="font-body text-caption text-mute">
                    {showInfo ? "Hide" : "Read more"}
                  </span>
                </div>
                <p className={`measure font-body text-body text-mute ${showInfo ? "" : "line-clamp-2"}`}>
                  {item.description}
                </p>
              </button>

              {/* One gold button, and outline buttons for the secondary exits.
                  This row used to carry four different treatments — a 9px
                  gold bar at weight 900, two mono caps links and a third
                  hover colour — for two levels of action. */}
              <div className="mt-6 flex flex-wrap items-center gap-3 border-t border-line pt-5">
                <ConversionCTA size="md" onClick={onClose} />
                {item.caseStudyUrl && (
                  <CTA to={item.caseStudyUrl} tone="secondary" size="md" onClick={onClose}>
                    Case study
                  </CTA>
                )}
                {item.isInstagram && item.instagramUrl && (
                  <CTA
                    href={item.instagramUrl}
                    tone="secondary"
                    size="md"
                    arrow={false}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <Instagram aria-hidden="true" className="h-4 w-4" /> Instagram
                  </CTA>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/* The default heading used to be "Cinema-Grade. / At AI Speed." — word for word
   the Services heading further down the same page. This section is about the
   work itself, so it now says so and Services keeps the positioning line. */
export const Showcase = ({
  niche = null,
  eyebrow = "Selected work / real creative",
  heading = "See what",
  headingAccent = "we make.",
  subheading = "Acquisition ads, micro drama, brand films, and explainers, made for India, the US, and beyond. Every piece here shipped to a client.",
  showFilters = true,
  id = "showcase",
}) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");
  const [open, setOpen] = useState(null);
  const [activePreview, setActivePreview] = useState(null);
  const headerRef = useRef(null);
  const railRef = useRef(null);
  const setWidthRef = useRef(0);
  const skipNextScroll = useRef(false);
  const animatingRef = useRef(false);
  const animFrameRef = useRef(null);
  const copyRef = useRef(null);

  // The committed seed is a snapshot of the CMS works list — if Strapi is down,
  // slow, or empty, visitors still see the portfolio instead of a blank section.
  useEffect(() => {
    fetch(`${STRAPI_URL}/api/works?sort=order:asc&populate=image`)
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status} ${r.statusText}`);
        const json = await r.json();
        const data = json?.data ?? [];
        if (data.length === 0) throw new Error("CMS returned 0 items");
        const cmsItems = data.map(mapStrapiItem);
        const cmsVideoUrls = new Set(cmsItems.map((item) => item.videoUrl).filter(Boolean));
        const localAdditions = SEED_WORKS
          .map(mapStrapiItem)
          .filter((item) => !item.videoUrl || !cmsVideoUrls.has(item.videoUrl));
        setItems(sortShowcaseItems([...cmsItems, ...localAdditions], { featuredOnly: false }));
      })
      .catch((err) => {
        window.posthog?.capture?.("showcase_cms_fallback", { error: String(err?.message || err) });
        setItems(sortShowcaseItems(SEED_WORKS.map(mapStrapiItem), { featuredOnly: false }));
      })
      .finally(() => setLoading(false));
  }, []);

  const nicheItems = niche
    ? items.filter((i) =>
        i.niche.split(",").map((s) => s.trim()).includes(niche)
      )
    : items;
  const filtered = filter === "All"
    ? nicheItems
    : nicheItems.filter((i) => (i.categories || [i.category]).includes(filter));

  // Switching filters or niche changes the card set. Stop any mounted remote
  // player first so a hidden iframe never keeps consuming bandwidth.
  useEffect(() => {
    setActivePreview(null);
  }, [filter, niche, filtered.length]);

  // Center the rail on the middle copy when items or filter change.
  // Measure setW from the first copy wrapper so it accounts for inner gaps + trailing margin precisely.
  useEffect(() => {
    if (!railRef.current || !copyRef.current || filtered.length === 0) return;
    const id = requestAnimationFrame(() => {
      const copyEl = copyRef.current;
      if (!copyEl || !railRef.current) return;
      const cs = window.getComputedStyle(copyEl);
      const marginR = parseFloat(cs.marginRight) || 0;
      const setW = copyEl.offsetWidth + marginR;
      setWidthRef.current = setW;
      skipNextScroll.current = true;
      railRef.current.scrollLeft = setW;
    });
    return () => cancelAnimationFrame(id);
  }, [filtered.length, filter]);

  // Wrap scrollLeft into the middle copy so the seam stays invisible
  const wrapScroll = () => {
    const el = railRef.current;
    const setW = setWidthRef.current;
    if (!el || !setW) return;
    const sl = el.scrollLeft;
    if (sl < setW * 0.5) {
      skipNextScroll.current = true;
      el.scrollLeft = sl + setW;
    } else if (sl > setW * 1.5) {
      skipNextScroll.current = true;
      el.scrollLeft = sl - setW;
    }
  };

  // Native scroll (drag, wheel, trackpad) — wrap the seam between events
  const onRailScroll = () => {
    if (!railRef.current) return;
    if (animatingRef.current) return; // arrow-button animation handles its own wrap
    if (skipNextScroll.current) {
      skipNextScroll.current = false;
      return;
    }
    wrapScroll();
  };

  // Custom RAF-tweened scroll for the arrow buttons. Wraps after the animation,
  // not during, so the user never sees a mid-animation teleport.
  const scrollBy = (dx) => {
    const el = railRef.current;
    if (!el) return;
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
    const start = el.scrollLeft;
    const startTime = performance.now();
    const duration = 700;
    animatingRef.current = true;

    const tick = (now) => {
      if (!railRef.current) {
        animatingRef.current = false;
        return;
      }
      const t = Math.min((now - startTime) / duration, 1);
      // ease-out quintic — feels smoother and lands more gently than cubic
      const eased = 1 - Math.pow(1 - t, 5);
      railRef.current.scrollLeft = start + dx * eased;
      if (t < 1) {
        animFrameRef.current = requestAnimationFrame(tick);
      } else {
        animFrameRef.current = null;
        animatingRef.current = false;
        wrapScroll();
      }
    };
    animFrameRef.current = requestAnimationFrame(tick);
  };

  // Cleanup if the component unmounts mid-animation
  useEffect(() => () => {
    if (animFrameRef.current) cancelAnimationFrame(animFrameRef.current);
  }, []);

  // The arrow buttons used to move a flat ±320px against a 260/280px card plus
  // a 12/16px gap, so a click never landed on a card boundary and the rail
  // never came to rest composed. Measure the real step from the DOM instead.
  const cardStep = (direction) => {
    const card = railRef.current?.querySelector('[data-testid^="showcase-card-"]');
    if (!card) return 296 * direction;
    const row = card.parentElement;
    const gap = row ? parseFloat(window.getComputedStyle(row).columnGap) || 0 : 0;
    return (card.offsetWidth + gap) * direction;
  };

  return (
    <Section
      id={id}
      data-reel-label="Selected Work"
      data-testid="showcase-section"
      className="overflow-hidden"
    >
      <div ref={headerRef}>
        <SectionHeader
          eyebrow={eyebrow}
          title={heading}
          accent={headingAccent}
          lede={subheading}
        />
      </div>

      {/* Controls. Filters are content controls, not the page's conversion
          action, so they take a bone active state — gold stays reserved for
          the one thing worth clicking. They are also set in body type at a
          real size: this is the section's primary control and it was 10px
          mono caps. */}
      <div className="mb-8 flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        {showFilters ? (
          <div role="group" aria-label="Filter work by type" className="flex flex-wrap gap-2">
            {FILTERS.map((f) => (
              <button
                key={f}
                type="button"
                onClick={() => setFilter(f)}
                aria-pressed={filter === f}
                className={`flex min-h-12 items-center border px-5 font-body text-body transition-colors duration-hover ease-gate ${
                  filter === f
                    ? "border-bone bg-bone text-ground"
                    : "border-line text-mute hover:border-bone hover:text-bone"
                }`}
              >
                {f}
              </button>
            ))}
          </div>
        ) : <span />}

        <div className="flex items-center gap-2 self-end md:self-auto">
          <button
            type="button"
            onClick={() => scrollBy(cardStep(-1))}
            className="flex h-12 w-12 items-center justify-center border border-line text-mute transition-colors duration-hover ease-gate hover:border-bone hover:text-bone"
            aria-label="Scroll work left"
          >
            <ChevronLeft aria-hidden="true" className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={() => scrollBy(cardStep(1))}
            className="flex h-12 w-12 items-center justify-center border border-line text-mute transition-colors duration-hover ease-gate hover:border-bone hover:text-bone"
            aria-label="Scroll work right"
          >
            <ChevronRight aria-hidden="true" className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Horizontal rail — three identical copy wrappers create a seamless loop */}
      <div className="relative">
        {/* Edge softening, at both breakpoints. These used to be `md:` only,
            so phone cards were hard-cut at the viewport edge — the breakpoint
            where the rail is most obviously scrollable got the least help. */}
        <div className="pointer-events-none absolute bottom-0 left-0 top-0 z-10 w-10 bg-gradient-to-r from-ground to-transparent" />
        <div className="pointer-events-none absolute bottom-0 right-0 top-0 z-10 w-10 bg-gradient-to-l from-ground to-transparent" />

        {loading ? (
          <div className="flex gap-3 overflow-hidden py-4 md:gap-4">
            {[0, 1, 2, 3, 4].map((i) => (
              <div key={i} className="w-[260px] shrink-0 border border-line md:w-[280px]">
                <div className="brand-frame w-full animate-pulse" />
                <div className="h-24" />
              </div>
            ))}
          </div>
        ) : (
          <div
            ref={railRef}
            onScroll={onRailScroll}
            /* py-4, not pb-4: `overflow-x: auto` makes the cross axis compute to
               auto as well, so the card's hover scale was being clipped along
               the top edge of the rail. */
            className="flex overflow-x-auto py-4"
            style={{ scrollbarWidth: "none", scrollBehavior: "auto" }}
          >
            {/* The rail starts centered on copy 1, so that copy is the accessible
                one; copies 0 and 2 are visual duplicates for the seamless loop —
                hidden from screen readers and removed from tab order. */}
            {filtered.length > 0 && [0, 1, 2].map((copyIdx) => (
              <div
                key={copyIdx}
                ref={copyIdx === 0 ? copyRef : null}
                aria-hidden={copyIdx !== 1}
                className="mr-3 flex shrink-0 gap-3 md:mr-4 md:gap-4"
              >
                {filtered.map((item, itemIndex) => (
                  <Card
                    key={`${item.id}-${copyIdx}`}
                    item={item}
                    onOpen={setOpen}
                    tabIndex={copyIdx !== 1 ? -1 : undefined}
                    previewKey={`${copyIdx}-${item.id}`}
                    isPreviewing={activePreview === `${copyIdx}-${item.id}`}
                    onPreviewStart={setActivePreview}
                    onPreviewEnd={(key) => setActivePreview((current) => (current === key ? null : current))}
                    priority={copyIdx === 1 && itemIndex < 4}
                  />
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* No closing CTA here. Services is the very next section and ends on the
          one conversion; two gold buttons a screen apart devalued both. */}
      {!loading && filtered.length === 0 && (
        <div className="mt-10 border-t border-rule pt-10">
          <p className="measure font-body text-body text-mute">
            {niche && nicheItems.length === 0
              ? "Work for this space drops soon. Ask us for the private reel."
              : "More work in this category, dropping soon."}
          </p>
        </div>
      )}

      <PlayerModal item={open} onClose={() => setOpen(null)} />
    </Section>
  );
};

export default Showcase;
